import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { fetch, pessimisticUpdate } from '@ngrx/router-store/data-persistence';
import { from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CurriculumQueries } from '.';
import { CurriculumInterface } from '../../+models';
import { CURRICULUM_SERVICE_TOKEN, CurriculumServiceInterface } from '../../curriculum/curriculum.service.interface';
import { DalState } from '../dal.state.interface';
import { EffectFeedback } from '../effect-feedback';
import { AddEffectFeedback } from '../effect-feedback/effect-feedback.actions';
import {
  CurriculumsActionTypes,
  CurriculumsLoadError,
  CurriculumsLoaded,
  LoadCurriculums,
  PublishCurriculum,
  PublishCurriculumContent,
  PublishCurriculumForGoalGroup,
  UpsertCurriculum,
} from './curriculum.actions';

@Injectable()
export class CurriculumEffects {
  loadCurriculums$ = createEffect(() =>
    this.actions.pipe(
      ofType(CurriculumsActionTypes.LoadCurriculums),
      concatLatestFrom(() => this.store.select(CurriculumQueries.getLoaded)),
      fetch({
        run: (action: LoadCurriculums, loaded: boolean) => {
          if (!action.payload.force && loaded) return;
          return this.curriculumService
            .getAllForUser(action.payload.userId)
            .pipe(map((curriculums) => new CurriculumsLoaded({ curriculums })));
        },
        onError: (action: LoadCurriculums, error) => {
          return new CurriculumsLoadError(error);
        },
      })
    )
  );

  publishCurriculum$ = createEffect(() =>
    this.actions.pipe(
      ofType(CurriculumsActionTypes.PublishCurriculum),
      pessimisticUpdate({
        run: (action: PublishCurriculum) => {
          const { learningAreaId } = action.payload;
          return this.curriculumService.publishCurriculum(learningAreaId).pipe(
            switchMap((curriculum: CurriculumInterface) => {
              const actions: Action[] = [
                new UpsertCurriculum({ curriculum }),
                new AddEffectFeedback({
                  effectFeedback: EffectFeedback.generateSuccessFeedback(
                    this.uuid(),
                    action,
                    'Het curriculum werd succesvol gepubliceerd.'
                  ),
                }),
              ];
              return from(actions);
            })
          );
        },
        onError: (action: PublishCurriculum, error: any) => {
          const errorFeedbackAction = new AddEffectFeedback({
            effectFeedback: EffectFeedback.generateErrorFeedback(
              this.uuid(),
              action,
              'Er was een probleem bij het exporteren.'
            ),
          });
          return errorFeedbackAction;
        },
      })
    )
  );

  publishCurriculumForGoalGroup$ = createEffect(() =>
    this.actions.pipe(
      ofType(CurriculumsActionTypes.PublishCurriculumForGoalGroup),
      pessimisticUpdate({
        run: (action: PublishCurriculumForGoalGroup) => {
          const { goalGroupId } = action.payload;
          return this.curriculumService.publishCurriculumForGoalGroup(goalGroupId).pipe(
            switchMap((curriculum: CurriculumInterface | null) => {
              const actions: Action[] = [];

              if (curriculum) {
                actions.push(new UpsertCurriculum({ curriculum }));
              }

              actions.push(
                new AddEffectFeedback({
                  effectFeedback: EffectFeedback.generateSuccessFeedback(
                    this.uuid(),
                    action,
                    'Het curriculum werd succesvol gepubliceerd.'
                  ),
                })
              );

              return from(actions);
            })
          );
        },
        onError: (action: PublishCurriculumForGoalGroup, error: any) => {
          const errorFeedbackAction = new AddEffectFeedback({
            effectFeedback: EffectFeedback.generateErrorFeedback(
              this.uuid(),
              action,
              'Er was een probleem bij het publiceren van het curriculum.'
            ),
          });
          return errorFeedbackAction;
        },
      })
    )
  );

  publishCurriculumContent$ = createEffect(() =>
    this.actions.pipe(
      ofType(CurriculumsActionTypes.PublishCurriculumContent),
      pessimisticUpdate({
        run: (action: PublishCurriculumContent) => {
          const { curriculumGuid } = action.payload;

          return this.curriculumService.publishCurriculumContent(curriculumGuid).pipe(
            map(() => {
              return new AddEffectFeedback({
                effectFeedback: EffectFeedback.generateSuccessFeedback(
                  this.uuid(),
                  action,
                  'De content voor het curriculum werd succesvol gepubliceerd.'
                ),
              });
            })
          );
        },
        onError: (action: PublishCurriculumContent, error: any) => {
          return new AddEffectFeedback({
            effectFeedback: EffectFeedback.generateErrorFeedback(
              this.uuid(),
              action,
              'Er was een probleem bij het publiceren van de content voor het curriculum.'
            ),
          });
        },
      })
    )
  );

  constructor(
    private actions: Actions,
    private store: Store<DalState>,
    @Inject('uuid') private uuid: () => string,
    @Inject(CURRICULUM_SERVICE_TOKEN)
    private curriculumService: CurriculumServiceInterface
  ) {}
}
